import * as RadixContextMenu from "@radix-ui/react-context-menu";
import { Handler, useDrag } from "@use-gesture/react";
import classNames from "classnames";
import { action, computed, IComputedValue, runInAction, untracked } from "mobx";
import { observer } from "mobx-react-lite";
import React, { useContext, useEffect, useRef, useState } from "react";
import { PopperLayer } from "react-atmosphere";
import { ActionType, Pane, PaneInput, PaneViewMode } from "../Types";
import { AnimatedPopout } from "../ui/AnimatedPopout";
import {
  RadixContextMenuContent,
  RadixContextMenuItem,
} from "../ui/ContextMenu";
import { FeatherIcon } from "../ui/FeatherIcon";
import { Tooltip } from "../ui/Tooltip";
import {
  generatePaneId,
  generatePaneInputId,
  getParentByClassName,
  isModifierKeyDown,
  throttle,
} from "../Utils";
import { canvasDispatch } from "./CanvasDispatch";
import {
  activePaneStates,
  canCreateNewPaneComputed,
  canvasAtom,
  canvasTransformComputed,
  canvasViewport,
  focusedPaneIdAtom,
  globalValuesMobx,
  isViewingCustomLayoutComputed,
  selectedPaneIdsAtom,
  shouldShowCanvasWiresComputed,
  transferPaneInputSourceBox,
} from "./CanvasState";
import { FocusPaneEmitter } from "./FocusPane";
import { setFocusPaneIdOnMount } from "./NewPane";
import { PaneComponentContext } from "./PaneComponentContext";
import { useDragPaneOutputHandler } from "./PaneInput";
import {
  convertViewportPositionToCanvasOffset,
  getPaneInputAutoName,
  getPaneInputsWithSource,
  PANE_CLASSNAME,
  roundPosition,
  validateVariableName,
} from "./SandboxUtils";
import { zoomToFit } from "./ToolbarZoomButton";

export const PANE_NAME_INPUT_CLASSNAME = "pane-name-input";

export const PaneNameInput = observer(
  ({
    paneId,
    paneComputed,
    shouldOutline = false,
  }: {
    paneId: string;
    paneComputed: IComputedValue<Pane | undefined>;
    shouldOutline?: boolean;
  }) => {
    const paneName = computed(() => paneComputed.get()?.name).get();
    const [paneNameFast, setPaneNameFast] = useState(paneName);
    const [throttleSetPaneName] = useState(() =>
      throttle((name: string | undefined) => {
        canvasDispatch([ActionType.SetPaneName, { paneId, name }]);
      }, 200)
    );
    useEffect(() => () => throttleSetPaneName.cancel(), []);
    useEffect(() => {
      setPaneNameFast(paneName);
    }, [paneName]);
    const placeholder = "name";
    if (isViewingCustomLayoutComputed.get()) {
      if (paneName === undefined || paneName === "") {
        return null;
      }
      return <div className="font-mono-natto">{paneName}</div>;
    }

    const isCollapsed = computed(
      () =>
        activePaneStates.get(paneId)?.viewMode.get() === PaneViewMode.Collapsed
    ).get();

    return (
      <div
        className={classNames(
          "h-6 relative -mr-1",
          isCollapsed ? "pointer-events-none" : undefined
        )}
      >
        <div className="whitespace-pre px-1 font-mono-natto invisible">
          {paneNameFast ?? placeholder}
        </div>
        <div className="absolute top-0 bottom-0 -left-px -right-px">
          <input
            type="text"
            value={paneNameFast ?? ""}
            onChange={(e) => {
              const value = e.target.value;
              setPaneNameFast(value);
              throttleSetPaneName(value === "" ? undefined : value);
            }}
            onBlur={() => {
              if (paneNameFast === "") {
                setPaneNameFast(undefined);
              }
            }}
            placeholder={placeholder}
            spellCheck="false"
            className={classNames(
              PANE_NAME_INPUT_CLASSNAME,
              "w-full font-mono-natto py-[3px] px-1 bottom-0 bg-transparent border hover:border-neutral-300 focus:border-neutral-400 outline-none rounded placeholder:text-neutral-300",
              shouldOutline ? "border-primary-400" : "border-transparent"
            )}
          />
        </div>
      </div>
    );
  }
);

export const PaneGlobalNameInput = observer(
  ({
    paneId,
    paneComputed,
    autoFocus = false,
    className,
  }: {
    paneId: string;
    paneComputed: IComputedValue<Pane | undefined>;
    autoFocus?: boolean;
    className?: string;
  }) => {
    const paneName = computed(() => paneComputed.get()?.name).get();
    const [globalNameLocal, setGlobalNameLocal] = useState(paneName);
    useEffect(() => {
      return action(() => {
        if (paneName !== undefined) {
          // TODO: handle when multiple panes have same global name
          delete globalValuesMobx[paneName];
        }
      });
    }, [paneName]);
    const inputRef = useRef<HTMLInputElement | null>(null);
    useEffect(() => {
      if (autoFocus) {
        inputRef.current!.focus();
      }
    }, []);

    const isNameAllowed =
      globalNameLocal === undefined ||
      (validateVariableName(globalNameLocal) &&
        !(globalNameLocal in globalValuesMobx && globalNameLocal !== paneName));

    const tooltipMessage =
      globalNameLocal !== undefined &&
      globalNameLocal in globalValuesMobx &&
      globalNameLocal !== paneName
        ? "duplicate"
        : undefined;

    const placeholder = "global name";

    return (
      <div className={classNames("flex items-center", className)}>
        <div className="h-6 relative">
          <div className="font-mono-natto whitespace-pre px-1 invisible">
            {globalNameLocal === "" || globalNameLocal === undefined
              ? placeholder
              : globalNameLocal}
          </div>
          <div className="absolute top-0 bottom-0 -left-px -right-px">
            <input
              value={globalNameLocal ?? ""}
              onChange={(e) => {
                setGlobalNameLocal(e.target.value);
              }}
              onBlur={() => {
                if (isNameAllowed && globalNameLocal !== undefined) {
                  if (paneName !== globalNameLocal) {
                    canvasDispatch([
                      ActionType.SetPaneName,
                      { paneId, name: globalNameLocal },
                    ]);
                  }
                } else {
                  setGlobalNameLocal(paneName);
                }
              }}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  const paneElement = getParentByClassName(
                    e.currentTarget!,
                    PANE_CLASSNAME
                  );
                  paneElement?.focus();
                }
              }}
              spellCheck="false"
              placeholder={placeholder}
              className={classNames(
                "w-full font-mono-natto py-[3px] px-1 bottom-0 border rounded outline-none transition",
                paneName === undefined
                  ? "bg-neutral-0 border-neutral-400 hover:border-neutral-500 focus:border-neutral-500"
                  : "bg-transparent border-transparent hover:border-neutral-300 focus:border-neutral-400",
                { "text-red-500": !isNameAllowed }
              )}
              ref={inputRef}
            />
          </div>
          {tooltipMessage !== undefined ? (
            <PopperLayer
              reference={inputRef}
              render={({ state, completeTransitionExit }) => (
                <AnimatedPopout
                  state={state}
                  completeTransitionExit={completeTransitionExit}
                  transformOrigin="bottom center"
                >
                  <div className="bg-neutral-700 text-neutral-50 rounded px-3 py-1 text-center">
                    {tooltipMessage}
                  </div>
                </AnimatedPopout>
              )}
              options={{
                placement: "top",
                modifiers: [{ name: "offset", options: { offset: [0, 2] } }],
              }}
              transitionExit={true}
            />
          ) : null}
        </div>
      </div>
    );
  }
);

function focusOutgoingPanes(paneId: string, outputIndex: number) {
  const outgoingPanes = canvasAtom.value.panes.filter((pane) => {
    if ("inputs" in pane) {
      if (
        pane.inputs.some(
          (input) =>
            input.source?.[0] === paneId && input.source[1] === outputIndex
        )
      ) {
        return true;
      }
    }
    if ("renderOutput" in pane) {
      if (
        pane.renderOutput?.[0] === "custom" &&
        pane.renderOutput[1]?.[0] === paneId &&
        pane.renderOutput[1][1] === outputIndex
      ) {
        return true;
      }
      if (
        pane.renderOutput?.[0] === "react" &&
        pane.renderOutput[1]?.ReactDOM?.[0] === paneId &&
        pane.renderOutput[1].ReactDOM[1] === outputIndex
      ) {
        return true;
      }
    }
    return false;
  });
  if (outgoingPanes.length === 1) {
    FocusPaneEmitter.emit("focus", { paneId: outgoingPanes[0].id });
  }
  if (outgoingPanes.length > 1) {
    runInAction(() => {
      focusedPaneIdAtom.value = undefined;
      selectedPaneIdsAtom.value = outgoingPanes.map((p) => p.id);
      zoomToFit(selectedPaneIdsAtom.value);
    });
  }
}

const TransformButtonContextMenu = observer(
  ({ paneId, outputIndex }: { paneId: string; outputIndex: number }) => {
    const numConnectedPaneInputs = computed(
      () =>
        getPaneInputsWithSource(canvasAtom.value.panes, [paneId, outputIndex])
          .length
    ).get();
    return (
      <RadixContextMenuContent>
        <RadixContextMenuItem
          onClick={(e) => {
            const position = convertViewportPositionToCanvasOffset(
              [e.clientX, e.clientY],
              canvasTransformComputed.get(),
              canvasViewport.zoom
            );
            // rAF to run this code outside the click handler
            // This will attach a click handler to window that we don't want to fire immediately
            requestAnimationFrame(
              action(() => {
                transferPaneInputSourceBox.set({
                  paneInputs: getPaneInputsWithSource(canvasAtom.value.panes, [
                    paneId,
                    outputIndex,
                  ]),
                  position,
                  originalPaneOutput: [paneId, outputIndex],
                  hoverPaneOutput: undefined,
                });
              })
            );
          }}
          disabled={numConnectedPaneInputs === 0}
        >
          transfer {numConnectedPaneInputs} output
          {numConnectedPaneInputs !== 1 ? "s" : ""}
        </RadixContextMenuItem>
      </RadixContextMenuContent>
    );
  }
);

export const PaneTransformButtonClassName = "transform-button";

export const PaneTransformButton = observer(
  ({
    paneId,
    label,
    outputIndex = 0,
    modifiedOutputIndex,
    tooltipText,
    paddingClassName,
  }: {
    paneId: string;
    label?: string;
    outputIndex?: number;
    modifiedOutputIndex?: number;
    tooltipText?: React.ReactNode;
    paddingClassName?: string;
  }) => {
    const { showPaneBodyOnCollapsedPaneBox } = useContext(PaneComponentContext);

    const bindDrag = useDrag(
      action<Handler<"drag">>(({ down, tap, first, last, xy, event, memo }) => {
        if (tap) {
          if (isModifierKeyDown(event.metaKey, event.ctrlKey)) {
            focusOutgoingPanes(paneId, outputIndex);
            return;
          }
          const pane = canvasAtom.value.panes.find((p) => p.id === paneId)!;
          const paneRect = pane.rect;
          const settledOutputIndex =
            isModifierKeyDown(event.metaKey, event.ctrlKey) &&
            modifiedOutputIndex !== undefined
              ? modifiedOutputIndex
              : outputIndex;
          const inputPaneName = getPaneInputAutoName(
            [],
            settledOutputIndex,
            pane,
            undefined
          );
          const paneInput: PaneInput = {
            id: generatePaneInputId(),
            name: inputPaneName,
            source: [paneId, settledOutputIndex ?? 0],
          };
          const newPaneId = generatePaneId();
          setFocusPaneIdOnMount(newPaneId);
          canvasDispatch([
            ActionType.CreateTransformPane,
            {
              paneId: newPaneId,
              position: [paneRect[0] + paneRect[2] + 80, paneRect[1]],
              paneInput,
              expression: inputPaneName ?? "",
            },
          ]);
          return;
        }
        if (first) {
          showPaneBodyOnCollapsedPaneBox.set(false);
        }

        return useDragPaneOutputHandler(
          memo,
          last,
          down,
          xy,
          paneId,
          () =>
            isModifierKeyDown(event.metaKey, event.ctrlKey) &&
            modifiedOutputIndex !== undefined
              ? modifiedOutputIndex
              : outputIndex,
          undefined
        );
      }),
      {
        filterTaps: true,
        transform: ([x, y]) =>
          untracked(() =>
            roundPosition(
              convertViewportPositionToCanvasOffset(
                [x, y],
                canvasTransformComputed.get(),
                canvasViewport.zoom
              )
            )
          ),
      }
    );

    if (!shouldShowCanvasWiresComputed.get()) {
      return null;
    }

    return (
      <div className="-mr-1">
        <RadixContextMenu.Root>
          <RadixContextMenu.Trigger>
            <Tooltip
              content={
                tooltipText ?? (
                  <div>
                    <div>
                      <span className="font-semibold">drag</span> to transform
                    </div>
                    <div>and connect panes</div>
                  </div>
                )
              }
              disabled={computed(
                () => transferPaneInputSourceBox.get() !== undefined
              ).get()}
            >
              <button
                {...(canCreateNewPaneComputed.get() ? bindDrag() : undefined)}
                className={classNames(
                  PaneTransformButtonClassName,
                  "flex items-center rounded-sm text-neutral-400 hover:text-neutral-700 transition-colors touch-none",
                  paddingClassName ?? "p-1"
                )}
              >
                {label !== undefined ? (
                  <span className="mr-1">{label}</span>
                ) : null}
                <FeatherIcon icon="arrow-right" size="sm" />
              </button>
            </Tooltip>
          </RadixContextMenu.Trigger>
          <RadixContextMenu.Portal>
            <div>
              <TransformButtonContextMenu
                paneId={paneId}
                outputIndex={outputIndex}
              />
            </div>
          </RadixContextMenu.Portal>
        </RadixContextMenu.Root>
      </div>
    );
  }
);
